// src/components/advanced/FormBuilder/hooks/useFieldTypeCatalogue.ts
/**
 * Loads the shared `CustomFieldType` catalogue that drives the builder's type dropdown and palette.
 *
 * Every custom-field screen needs the same list, so this lives here rather than being redeclared per
 * screen. It was previously copy-pasted verbatim into `CourseCustomField.tsx` and
 * `SpecialSessionFormBuilder.tsx`; adding a third copy for needs is what prompted hoisting it.
 *
 * Returns `null` while loading, so callers can gate on it before building an adapter.
 */
import { useEffect, useState } from 'react';
import { useCustomFieldTypeService, type CustomFieldType } from '@actschurch/ccms-sdk';

export function useFieldTypeCatalogue(): CustomFieldType[] | null {
  const customFieldTypeService = useCustomFieldTypeService();
  const [fieldTypes, setFieldTypes] = useState<CustomFieldType[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    customFieldTypeService
      .list()
      .then((types) => {
        if (!cancelled) setFieldTypes(types);
      })
      .catch((e) => {
        // ⚠️ The original copies had no catch, so a failed catalogue left the screen on a spinner
        // for ever with nothing in the console. An empty list at least lets `FormBuilder` fall back
        // to its built-in FIELD_TYPES.
        console.error('[useFieldTypeCatalogue] failed to load custom field types:', e);
        if (!cancelled) setFieldTypes([]);
      });
    return () => {
      cancelled = true;
    };
  }, [customFieldTypeService]);

  return fieldTypes;
}
